import * as React from 'react'
import { MessageCircleQuestion, Pencil, Square, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  bindSessionPaintBranch,
  paintSelectionFromAgentPill,
  pillKeyForPaintSelection,
  selectSessionPaintSelection,
  setSessionPaintSelection,
  useAnswerIngestQuestionMutation,
  useAppDispatch,
  useAppSelector,
  useCancelIngestMessageMutation,
  useSendIngestMessageMutation,
  type AnsweredIngestQuestion,
  type IngestCotByPill,
  type IngestCotCommittedTurn,
  type IngestOperatorMessage,
  type IngestSection,
  type PendingIngestQuestion,
  type ProxyRunWithChildren
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { ScrollArea } from '../ui/scroll-area'
import { LazyChatMarkdown } from '../ChatMarkdown/LazyChatMarkdown'
import { extractAssistantTurnTexts } from '../ToolTrace/MessageList'
import { cn } from '../../lib/utils'
import { WriteButton } from '../../pages/IngestPage/shared/WriteButton'
import { ingestQuestionElementId } from './ingest-session-route'
import { AgentPillStrip } from './AgentPillStrip'
import { IngestConfirmedWritesPanel } from './IngestConfirmedWritesPanel'
import { useDocumentUpload } from './DocumentUploadProvider'
import {
  buildIngestAgentPills,
  isFinishedChildPillStatus,
  LEAD_PILL_KEY,
  liveTailForPill,
  messageBelongsToPill,
  newestWaitingPillKey,
  questionBelongsToPill,
  resolvePillKeyForQuestion,
  runSourceLabel,
  sectionIdFromRun,
  type IngestAgentPill
} from './ingestAgentPills'
import {
  clampAgentAtForOperatorMessages,
  operatorMessageDisplayAt,
  operatorMessageSortAt
} from './operatorMessageClocks'

function dataTransferHasFiles(dt: DataTransfer | null): boolean {
  if (!dt) return false
  return Array.from(dt.types).includes('Files')
}

/** Free-text POST body fields for the selected pill (omit both → lead). */
function freeTextTarget(pill: IngestAgentPill | undefined): {
  dispatchId?: string
  agentKind?: 'ingest_section_parser' | 'ingest_reconciler'
} {
  if (!pill || pill.key === LEAD_PILL_KEY || !pill.dispatchId) return {}
  const agentKind =
    pill.agentKind === 'ingest_section_parser' || pill.agentKind === 'ingest_reconciler'
      ? pill.agentKind
      : undefined
  return {
    dispatchId: pill.dispatchId,
    ...(agentKind ? { agentKind } : {})
  }
}

// Fades the top edge of the history viewport so older exchanges dissolve upward
// as you scroll into them. Applied only once the thread is scrolled off the top
// (see `topFaded`), so a short, unscrolled thread stays fully crisp. Kept in
// sync for Safari via the -webkit- twin.
const FADE_MASK =
  '[mask-image:linear-gradient(to_bottom,transparent,#000_3.5rem)] [-webkit-mask-image:linear-gradient(to_bottom,transparent,#000_3.5rem)]'

interface ConversationProps {
  jobId: string
  /** Job graph branch — required so confirmed Apply lands off main. */
  branchId?: string
  pending: readonly PendingIngestQuestion[]
  answered: readonly AnsweredIngestQuestion[]
  /** Operator-initiated messages, woven into the history thread (oldest first). */
  messages: readonly IngestOperatorMessage[]
  /**
   * The agent's persisted conversation runs. Their assistant prose is the
   * agent's chain-of-thought, rendered inline (markdown) like the orchestrator
   * sidebar. Tool calls stay in the Activity Log, not here.
   */
  transcript?: readonly ProxyRunWithChildren[]
  /**
   * Active section catalog rows. Each becomes a pill as soon as `create_section`
   * lands (polled), before any parser CoT streams.
   */
  sections?: readonly Pick<IngestSection, 'id' | 'name' | 'status'>[]
  /**
   * Live CoT tails sharded by agent pill (`useIngestCotStream().byPill`).
   * Preferred over `liveCot` / `liveCommitted` when present.
   */
  byPill?: IngestCotByPill
  /**
   * Live, in-flight chain-of-thought for the current turn, streamed over SSE
   * (`useIngestCotStream`). Rendered as a trailing bubble below the durable
   * thread while non-empty; clears when the turn commits and the polled
   * `transcript` picks it up. Lead-only convenience when `byPill` is absent.
   */
  liveCot?: string
  /**
   * Turns that finished streaming but may not be in the polled transcript yet
   * (`useIngestCotStream`). Rendered after the durable thread — deduped against
   * it — so committed text doesn't blink out for the ~2 s until the poll lands.
   */
  liveCommitted?: readonly IngestCotCommittedTurn[]
  /** The ingested file's display name — drives the always-present opening
   * "Starting ingest on …" row (rendered by the UI, not narrated by the agent). */
  fileName?: string
  /** When the job was submitted (ISO) — timestamps the opening row. */
  startedAt?: string
  /** Optional operator guidance captured at upload — shown under the opening row. */
  additionalContext?: string
  /** Run is non-terminal — gates the always-on composer. */
  active: boolean
  /** Render durable history only; hides every messaging and approval control. */
  readOnly?: boolean
  /** When `active` is false, stale non-terminal pill statuses map to this. */
  jobEndStatus?: 'done' | 'failed' | 'aborted'
  /** Controls whether the conversation uses the page height or fills its rail. */
  layout: 'rail' | 'page'
  /** Deep-link target for a parked `user_question` (`?question=`). */
  focusQuestionId?: string | null
  /** Stop the agent run while keeping the job branch. */
  onStop?: () => void
  /** Disables Stop while the request is in flight. */
  stopping?: boolean
  /** Without `ingest/manage`, Stop stays visible but disabled with the reason. */
  canManage?: boolean
  /** True while an Add documents transfer is in flight (disables card drop). */
  documentTransferOpen?: boolean
  /** Card-scoped file drop → parent opens the Add documents modal. */
  onDropDocuments?: (files: File[]) => void
}

/** One time-ordered thread entry: agent CoT, a resolved Q&A, or an operator message. */
type ThreadItem =
  | {
      kind: 'cot'
      at: string
      key: string
      text: string
      source?: string
      /** Owning nested run id; absent for lead CoT. */
      runId?: string
      sectionId?: string
      agentKind?: string
      /** Committing tool_use name, when this text preceded a tool call. */
      toolName?: string
    }
  | { kind: 'answered'; at: string; key: string; answered: AnsweredIngestQuestion }
  | { kind: 'message'; at: string; key: string; message: IngestOperatorMessage }

/**
 * Pull the agents' chain-of-thought from the persisted transcript. The prose
 * extraction (leading assistant text before each turn's committing tool_use)
 * is shared with the Activity Log via `extractAssistantTurnTexts`; this walker
 * only threads the run tree, stamps each segment with its message timestamp +
 * a stable key, and carries the owning run's attribution tag so subagent
 * turns are distinguishable from the lead's. Operator turns are `role: 'user'`
 * so they aren't double-counted here (they come from the `messages` rail).
 */
function extractCot(transcript: readonly ProxyRunWithChildren[]): ThreadItem[] {
  const out: ThreadItem[] = []
  let seq = 0
  const walk = (runs: readonly ProxyRunWithChildren[]): void => {
    for (const run of runs) {
      const nested =
        run.agent_kind === 'ingest_section_parser' || run.agent_kind === 'ingest_reconciler'
      const source = runSourceLabel(run)
      const sectionId = sectionIdFromRun(run)
      for (const msg of run.messages ?? []) {
        for (const turn of extractAssistantTurnTexts([msg])) {
          out.push({
            kind: 'cot',
            at: msg.created_at,
            key: `cot-${seq++}`,
            text: turn.text,
            source,
            toolName: turn.toolName,
            ...(nested
              ? {
                  runId: run.id,
                  agentKind: run.agent_kind,
                  ...(sectionId ? { sectionId } : {})
                }
              : {})
          })
        }
      }
      walk(run.children ?? [])
    }
  }
  walk(transcript)
  return out
}

/**
 * Narration that immediately preceded `ask_user_question` must sort before
 * that question. Persisted message timestamps can land after the write's
 * `askedAt` (assistant row flushed after the tool runs); clamp those CoT
 * rows to just before the matching question.
 */
function clampAskCotBeforeQuestions(
  items: ThreadItem[],
  answered: readonly AnsweredIngestQuestion[]
): ThreadItem[] {
  return items.map((item) => {
    if (item.kind !== 'cot' || item.toolName !== 'ask_user_question') return item
    const match = answered.find(
      (a) =>
        (item.runId != null && a.agentRunId === item.runId) ||
        (item.sectionId != null && a.sectionId === item.sectionId)
    )
    if (!match?.askedAt || item.at.localeCompare(match.askedAt) <= 0) return item
    const askedMs = Date.parse(match.askedAt)
    if (Number.isNaN(askedMs)) return item
    return { ...item, at: new Date(askedMs - 1).toISOString() }
  })
}

function threadKindRank(kind: ThreadItem['kind']): number {
  switch (kind) {
    case 'cot':
      return 0
    case 'answered':
      return 1
    case 'message':
      return 2
  }
}

function compareThreadItems(x: ThreadItem, y: ThreadItem): number {
  const byAt = x.at.localeCompare(y.at)
  if (byAt !== 0) return byAt
  return threadKindRank(x.kind) - threadKindRank(y.kind)
}

/** Exported for unit tests — time-ordered merge of Q&A, operator notes, and CoT. */
export function buildThread(
  answered: readonly AnsweredIngestQuestion[],
  messages: readonly IngestOperatorMessage[],
  transcript: readonly ProxyRunWithChildren[]
): ThreadItem[] {
  const delivered = messages.filter((m) => !m.pending)
  const items: ThreadItem[] = [
    ...answered.map(
      (a): ThreadItem => ({
        kind: 'answered',
        // Place the exchange where the question was asked so preceding status
        // narration sorts above it; the answer still renders inside the row.
        at: a.askedAt || a.answeredAt,
        key: `a-${a.writeId}`,
        answered: a
      })
    ),
    ...delivered.map(
      (m): ThreadItem => ({
        kind: 'message',
        at: operatorMessageSortAt(m),
        key: `m-${m.writeId}`,
        message: m
      })
    ),
    ...extractCot(transcript)
  ]
  return clampAskCotBeforeQuestions(items, answered)
    .map((item) => {
      if (item.kind === 'message') return item
      const at = clampAgentAtForOperatorMessages(item.at, delivered)
      return at === item.at ? item : { ...item, at }
    })
    .sort(compareThreadItems)
}

/**
 * The agent ↔ operator conversation for an ingest job, styled to mirror the
 * Industrial Agent sidebar chat: a scrollable thread of operator notes and
 * agent reasoning (oldest fading up top, newest pinned at the bottom), the
 * current open question(s) rendered interactive in place, and an always-on
 * composer at the bottom. Answering a question settles its `agent_writes` row
 * (the run's settlement poll resumes it in-turn); a free message queues a
 * durable `user_message` row the run drains at its next turn. The note appears
 * in history as soon as it is sent (after live CoT from the unread turn) and
 * stays before the turn that actually reads it.
 */
export function Conversation({
  jobId,
  branchId,
  pending,
  answered,
  messages,
  transcript,
  sections,
  byPill,
  liveCot,
  liveCommitted,
  fileName,
  startedAt,
  additionalContext,
  active,
  readOnly = false,
  jobEndStatus = 'done',
  layout,
  focusQuestionId,
  onStop,
  stopping = false,
  canManage = false,
  documentTransferOpen = false,
  onDropDocuments
}: ConversationProps) {
  const historyRef = React.useRef<HTMLDivElement>(null)
  const historyViewport = React.useCallback((): HTMLElement | null => {
    const node = historyRef.current
    if (!node) return null
    return node.closest<HTMLElement>('[data-slot=scroll-area-viewport]') ?? node
  }, [])
  const dispatch = useAppDispatch()
  const paintSelection = useAppSelector(selectSessionPaintSelection)
  const { documentStagingVersion } = useDocumentUpload()

  const pills = React.useMemo(
    () =>
      buildIngestAgentPills({
        transcript: transcript ?? [],
        byPill,
        pending,
        sections,
        jobActive: active,
        jobEndStatus
      }),
    [transcript, byPill, pending, sections, active, jobEndStatus]
  )

  const [selectedKey, setSelectedKey] = React.useState<string>(LEAD_PILL_KEY)
  const selectedPill = pills.find((p) => p.key === selectedKey) ?? pills[0]
  const [dragActive, setDragActive] = React.useState(false)
  const dragDepthRef = React.useRef(0)

  const selectPill = React.useCallback(
    (key: string) => {
      setSelectedKey(key)
      if (readOnly) return
      const pill = pills.find((entry) => entry.key === key)
      dispatch(setSessionPaintSelection(paintSelectionFromAgentPill(pill)))
    },
    [dispatch, pills, readOnly]
  )
  const previousDocumentStagingVersion = React.useRef(documentStagingVersion)

  React.useEffect(() => {
    if (readOnly) return
    if (documentStagingVersion === previousDocumentStagingVersion.current) return
    previousDocumentStagingVersion.current = documentStagingVersion
    if (selectedKey !== LEAD_PILL_KEY) {
      selectPill(LEAD_PILL_KEY)
      toast.info('Documents are job-level, so this view switched to Lead.')
    }
  }, [documentStagingVersion, readOnly, selectPill, selectedKey])

  React.useEffect(() => {
    if (readOnly) return
    dispatch(bindSessionPaintBranch(branchId))
  }, [branchId, dispatch, readOnly])

  // Plant dropdown (or any shared-scope writer) → matching agent pill.
  React.useEffect(() => {
    if (readOnly) return
    const nextKey = pillKeyForPaintSelection(paintSelection, pills, selectedKey)
    if (nextKey !== selectedKey && pills.some((pill) => pill.key === nextKey)) {
      setSelectedKey(nextKey)
    }
  }, [paintSelection, pills, readOnly, selectedKey])

  // Autoswitch to the newest waiting pill when a question appears.
  const waitingKey = newestWaitingPillKey(pills, pending)
  const prevWaitingRef = React.useRef<string | undefined>(undefined)
  React.useEffect(() => {
    if (readOnly) return
    if (waitingKey && waitingKey !== prevWaitingRef.current) {
      selectPill(waitingKey)
    }
    prevWaitingRef.current = waitingKey
  }, [readOnly, selectPill, waitingKey])

  React.useEffect(() => {
    if (readOnly || !focusQuestionId) return
    const question = pending.find((entry) => entry.writeId === focusQuestionId)
    if (!question) return
    const key = resolvePillKeyForQuestion(pills, question)
    if (key !== selectedKey) selectPill(key)
  }, [focusQuestionId, pending, pills, readOnly, selectPill, selectedKey])

  // Keep selection valid if pills change (e.g. refresh).
  React.useEffect(() => {
    if (!pills.some((p) => p.key === selectedKey) && pills[0]) {
      selectPill(pills[0].key)
    }
  }, [pills, selectPill, selectedKey])

  // Delivered operator messages live in the time-ordered thread at send time.
  // Still-queued ones sit in the same scrollable history after live CoT (the
  // unread in-flight turn) with cancel / edit, so the bubble is visible as soon
  // as the operator hits send. Both are scoped to the selected pill so child
  // free-text never appears on Lead (and vice versa).
  const pillKey = selectedPill?.key ?? LEAD_PILL_KEY
  const pillDispatchId = selectedPill?.dispatchId
  const delivered = React.useMemo(
    () => messages.filter((m) => !m.pending && messageBelongsToPill(m, pillKey, pillDispatchId)),
    [messages, pillKey, pillDispatchId]
  )
  const pillQueued = React.useMemo(
    () => messages.filter((m) => m.pending && messageBelongsToPill(m, pillKey, pillDispatchId)),
    [messages, pillKey, pillDispatchId]
  )

  const pillPending = React.useMemo(
    () => pending.filter((q) => questionBelongsToPill(pills, q, pillKey)),
    [pending, pills, pillKey]
  )
  const pillAnswered = React.useMemo(
    () => answered.filter((q) => questionBelongsToPill(pills, q, pillKey)),
    [answered, pills, pillKey]
  )

  const messageTarget = freeTextTarget(selectedPill)
  const childFinished =
    selectedPill != null &&
    selectedPill.key !== LEAD_PILL_KEY &&
    isFinishedChildPillStatus(selectedPill.status)

  // Finished children never drain free-text — cancel orphans so they don't sit forever.
  const [cancelOrphanMessage] = useCancelIngestMessageMutation()
  const cancellingOrphansRef = React.useRef(new Set<string>())
  React.useEffect(() => {
    if (readOnly) return
    const finishedDispatchIds = new Set(
      pills
        .filter(
          (p) =>
            p.key !== LEAD_PILL_KEY && isFinishedChildPillStatus(p.status) && Boolean(p.dispatchId)
        )
        .map((p) => p.dispatchId as string)
    )
    if (finishedDispatchIds.size === 0) return
    for (const m of messages) {
      if (!m.pending || !m.dispatchId || !finishedDispatchIds.has(m.dispatchId)) continue
      if (cancellingOrphansRef.current.has(m.writeId)) continue
      cancellingOrphansRef.current.add(m.writeId)
      void cancelOrphanMessage({ jobId, writeId: m.writeId })
        .unwrap()
        .finally(() => {
          cancellingOrphansRef.current.delete(m.writeId)
        })
    }
  }, [pills, messages, jobId, cancelOrphanMessage, readOnly])

  const thread = React.useMemo(() => {
    const full = buildThread(pillAnswered, delivered, transcript ?? [])
    if (pillKey === LEAD_PILL_KEY) {
      // Lead: lead CoT (no nested runId) + lead-targeted operator messages + lead Q&A.
      return full.filter(
        (t) => t.kind === 'message' || t.kind === 'answered' || (t.kind === 'cot' && !t.runId)
      )
    }
    return full.filter((t) => {
      if (t.kind === 'answered' || t.kind === 'message') return true
      if (t.kind === 'cot') {
        if (selectedPill?.agentRunId && t.runId === selectedPill.agentRunId) return true
        if (selectedPill?.sectionId && t.sectionId === selectedPill.sectionId) return true
        return false
      }
      return false
    })
  }, [pillAnswered, delivered, transcript, selectedPill, pillKey])

  const selectedLive = liveTailForPill(byPill, selectedPill)
  const liveText = (
    selectedLive?.liveText ??
    (selectedPill?.key === LEAD_PILL_KEY ? liveCot : '') ??
    ''
  ).trim()
  const hasLive = liveText.length > 0
  const selectedCommitted =
    selectedLive?.committed ?? (selectedPill?.key === LEAD_PILL_KEY ? liveCommitted : undefined)

  // Committed-over-SSE turns the polled transcript hasn't caught up to yet:
  // keep them rendered (instead of blinking out for a poll cycle) and drop each
  // one as soon as the durable thread carries it.
  const pendingCommitted = React.useMemo(() => {
    if (!selectedCommitted || selectedCommitted.length === 0) return []
    const cots = thread.filter((t) => t.kind === 'cot').map((t) => t.text)
    return selectedCommitted.filter(
      (turn) => !cots.some((c) => c === turn.text || c.includes(turn.text))
    )
  }, [selectedCommitted, thread])

  // Merge held SSE commits into the time-ordered thread so a "about to ask"
  // status note cannot render below the Q&A it preceded.
  const timeline = React.useMemo(() => {
    type TimelineItem =
      | { kind: 'thread'; at: string; key: string; item: ThreadItem }
      | { kind: 'held'; at: string; key: string; text: string; live: true }

    const held: TimelineItem[] = pendingCommitted.map((turn, i) => {
      let at = turn.at
      // Race: progress-commit can be stamped after the write's askedAt. Keep
      // pre-ask narration above an open question, or above an exchange when the
      // commit landed in the ask→answer window.
      for (const q of pillPending) {
        if (q.askedAt && at.localeCompare(q.askedAt) > 0) {
          const askedMs = Date.parse(q.askedAt)
          if (!Number.isNaN(askedMs)) at = new Date(askedMs - 1).toISOString()
        }
      }
      for (const q of pillAnswered) {
        if (
          q.askedAt &&
          q.answeredAt &&
          at.localeCompare(q.askedAt) > 0 &&
          at.localeCompare(q.answeredAt) <= 0
        ) {
          const askedMs = Date.parse(q.askedAt)
          if (!Number.isNaN(askedMs)) at = new Date(askedMs - 1).toISOString()
        }
      }
      at = clampAgentAtForOperatorMessages(at, delivered)
      return { kind: 'held', at, key: `pc-${turn.at}-${i}`, text: turn.text, live: true as const }
    })

    const fromThread: TimelineItem[] = thread.map((item) => ({
      kind: 'thread',
      at: item.at,
      key: item.key,
      item
    }))

    return [...fromThread, ...held].sort((a, b) => a.at.localeCompare(b.at))
  }, [thread, pendingCommitted, pillAnswered, pillPending, delivered])

  // Fade the top edge only once the thread is scrolled off the top, so a short,
  // unscrolled thread stays fully crisp and text resolves to full opacity as it
  // scrolls down away from the fade.
  const [topFaded, setTopFaded] = React.useState(false)
  const syncTopFade = React.useCallback((el: HTMLElement) => {
    setTopFaded(el.scrollTop > 6)
  }, [])

  React.useEffect(() => {
    const viewport = historyViewport()
    if (!viewport) return
    const onScroll = (): void => syncTopFade(viewport)
    viewport.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => viewport.removeEventListener('scroll', onScroll)
  }, [historyViewport, selectedKey, syncTopFade])

  // Composer text is lifted so "edit" on a queued message can refill it.
  const [composerText, setComposerText] = React.useState('')

  // Pin the history to the newest entry so it sits right above the divider;
  // re-pin whenever the thread grows or the live tail streams in.
  React.useEffect(() => {
    const viewport = historyViewport()
    if (!viewport) return
    viewport.scrollTop = viewport.scrollHeight
    syncTopFade(viewport)
  }, [
    thread.length,
    timeline.length,
    liveText,
    pillQueued.length,
    selectedKey,
    historyViewport,
    syncTopFade
  ])

  // The opening "Starting ingest on …" row renders as soon as the job row has
  // loaded, so the thread never opens cold — Lead pill only.
  const showStart = pillKey === LEAD_PILL_KEY && Boolean(fileName || startedAt)
  const hasHistory = showStart || timeline.length > 0
  const hasPending = pillPending.length > 0
  const hasQueued = pillQueued.length > 0

  if (!hasHistory && !hasLive && !hasPending && !hasQueued && !active && pills.length <= 1) {
    return (
      <div className="rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
        No conversation yet. The agent will ask here if it hits a decision only you can make.
      </div>
    )
  }

  const collapsedSelected =
    selectedPill &&
    selectedPill.key !== LEAD_PILL_KEY &&
    isFinishedChildPillStatus(selectedPill.status) &&
    !hasLive &&
    thread.length === 0 &&
    !hasPending &&
    !hasQueued

  const sectionNotStarted =
    selectedPill &&
    selectedPill.key !== LEAD_PILL_KEY &&
    selectedPill.status === 'pending' &&
    !hasLive &&
    thread.length === 0 &&
    pendingCommitted.length === 0 &&
    !hasPending &&
    !hasQueued

  return (
    // The page keeps its existing 36rem card while the rail fills all remaining
    // height beneath its compact session header. Drag handlers stay on this
    // card only — not window / AppLayout / Plant.
    <div
      className={cn(
        'relative flex min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm',
        layout === 'page' ? 'h-[36rem]' : 'min-h-0 flex-1 rounded-none border-0 shadow-none',
        layout === 'rail' && 'bg-transparent'
      )}
      onDragEnter={(event) => {
        if (!dataTransferHasFiles(event.dataTransfer)) return
        event.preventDefault()
        if (documentTransferOpen || !onDropDocuments) return
        dragDepthRef.current += 1
        setDragActive(true)
      }}
      onDragOver={(event) => {
        if (!dataTransferHasFiles(event.dataTransfer)) return
        event.preventDefault()
        if (documentTransferOpen || !onDropDocuments) return
        event.dataTransfer.dropEffect = 'copy'
      }}
      onDragLeave={(event) => {
        if (!dataTransferHasFiles(event.dataTransfer)) return
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
        if (dragDepthRef.current === 0) setDragActive(false)
      }}
      onDrop={(event) => {
        if (!dataTransferHasFiles(event.dataTransfer)) return
        event.preventDefault()
        dragDepthRef.current = 0
        setDragActive(false)
        if (documentTransferOpen || !onDropDocuments) return
        const files = Array.from(event.dataTransfer.files ?? [])
        if (files.length === 0) return
        onDropDocuments(files)
      }}
    >
      {dragActive && !documentTransferOpen ? (
        <div
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-background/80 px-4 text-center text-sm font-medium text-foreground"
          aria-hidden
        >
          Drop documents to add to this job
        </div>
      ) : null}
      <div className="flex min-h-0 w-full flex-1 flex-col">
        <AgentPillStrip
          pills={pills}
          selectedKey={selectedPill?.key ?? LEAD_PILL_KEY}
          onSelect={selectPill}
        />

        <ScrollArea
          className={cn(
            'min-h-0 flex-1 [&>[data-slot=scroll-area-viewport]>div]:!block',
            topFaded && FADE_MASK
          )}
        >
          {/* min-h-full + justify-end grows a short thread up from the composer
            instead of stranding it at the top of the pane. */}
          <div ref={historyRef} className="flex min-h-full flex-col justify-end px-3 py-1.5">
            {collapsedSelected ? (
              <div className="py-3 text-xs text-muted-foreground">
                {selectedPill.summary ?? `${selectedPill.label} finished.`}
              </div>
            ) : null}
            {sectionNotStarted ? (
              <div className="py-3 text-xs text-muted-foreground">
                Waiting for a section parser to start for this workstream.
              </div>
            ) : null}
            {/* Deterministic opening row from the job metadata — every run starts
              with this, independent of what the agent chooses to narrate. */}
            {showStart ? (
              <LogRow at={startedAt}>
                <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                  <div>
                    Starting ingest on{' '}
                    <span className="font-medium text-foreground">
                      {fileName ?? 'uploaded files'}
                    </span>
                  </div>
                  {additionalContext?.trim() ? (
                    <div className="whitespace-pre-wrap break-words text-xs">
                      <span className="font-medium text-foreground">Context:</span>{' '}
                      {additionalContext.trim()}
                    </div>
                  ) : null}
                </div>
              </LogRow>
            ) : null}
            {timeline.map((entry, i) => {
              const divided = showStart || i > 0
              if (entry.kind === 'held') {
                return (
                  <LogRow key={entry.key} live divided={divided}>
                    <CotBlock text={entry.text} />
                  </LogRow>
                )
              }
              const t = entry.item
              const displayAt = t.kind === 'message' ? operatorMessageDisplayAt(t.message) : t.at
              return (
                <LogRow key={t.key} at={displayAt} divided={divided}>
                  {t.kind === 'answered' ? (
                    <AnsweredExchange item={t.answered} />
                  ) : t.kind === 'message' ? (
                    <OperatorNote>{t.message.message}</OperatorNote>
                  ) : (
                    <CotBlock text={t.text} source={t.source} />
                  )}
                </LogRow>
              )
            })}
            {hasLive ? (
              <LogRow live divided={showStart || timeline.length > 0}>
                <LiveCot text={liveText} />
              </LogRow>
            ) : null}
            {hasQueued ? (
              <PendingOperatorNotes
                jobId={jobId}
                messages={pillQueued}
                divided={showStart || timeline.length > 0 || hasLive}
                readOnly={readOnly}
                onEdit={(text) => setComposerText(text)}
              />
            ) : null}
          </div>
        </ScrollArea>

        <div>
          {readOnly ? null : (
            <IngestConfirmedWritesPanel jobId={jobId} branchId={branchId} active={active} />
          )}

          {hasPending && !readOnly ? (
            <div
              className={cn(
                'flex min-h-0 flex-col gap-4 overflow-y-auto px-4 py-3',
                (hasHistory || hasLive) && 'border-t border-border'
              )}
            >
              {pillPending.map((q) => (
                // With a single open question the bottom composer answers it, so
                // the card carries only the question + option buttons; a second
                // stacked textarea appears only in the (rare) multi-question case.
                <OpenQuestion
                  key={q.writeId}
                  jobId={jobId}
                  question={q}
                  showComposer={pillPending.length > 1}
                />
              ))}
            </div>
          ) : null}

          {readOnly ? null : (
            <OperatorComposer
              jobId={jobId}
              active={active}
              value={composerText}
              onChange={setComposerText}
              bordered={hasHistory || hasLive || hasPending || hasQueued}
              empty={!hasHistory && !hasLive && !hasPending && !hasQueued}
              answerTarget={pillPending.length === 1 ? pillPending[0] : undefined}
              messageTarget={messageTarget}
              selectedPillKey={pillKey}
              targetLabel={selectedPill?.label}
              closedChild={
                childFinished
                  ? {
                      label: selectedPill?.label ?? 'This agent',
                      onSwitchToLead: () => selectPill(LEAD_PILL_KEY)
                    }
                  : undefined
              }
              onStop={onStop}
              stopping={stopping}
              canManage={canManage}
            />
          )}
        </div>
      </div>
    </div>
  )
}

/** Clock time for a log entry (`HH:MM:SS`, 24h), or '' for a missing/bad date. */
function formatLogTime(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })
}

/**
 * Still-queued operator notes in the scrollable history — after live CoT from
 * the unread turn, before the composer. Cancel deletes the un-consumed row;
 * edit pulls it back into the composer. The agent reads the queue on its next
 * step, at which point the note joins the durable thread at send time.
 */
function PendingOperatorNotes({
  jobId,
  messages,
  divided,
  readOnly,
  onEdit
}: {
  jobId: string
  messages: readonly IngestOperatorMessage[]
  divided: boolean
  readOnly: boolean
  onEdit: (text: string) => void
}) {
  const [cancelMessage, { isLoading: cancelling }] = useCancelIngestMessageMutation()
  const [error, setError] = React.useState<string | null>(null)

  const cancel = async (writeId: string): Promise<boolean> => {
    try {
      await cancelMessage({ jobId, writeId }).unwrap()
      setError(null)
      return true
    } catch {
      setError('Cancel failed — the message may already be delivered to the agent.')
      return false
    }
  }
  // Refill the composer only once the cancel lands; otherwise the original
  // stays queued and resending the edit would deliver both.
  const edit = async (m: IngestOperatorMessage): Promise<void> => {
    if (await cancel(m.writeId)) onEdit(m.message)
  }

  return (
    <>
      {error ? <p className="m-0 py-2 text-xs text-destructive">{error}</p> : null}
      {messages.map((m, i) => (
        <LogRow
          key={m.writeId}
          at={operatorMessageDisplayAt(m)}
          divided={divided || i > 0 || Boolean(error)}
        >
          <OperatorNote
            queued
            cancelling={cancelling}
            onEdit={readOnly ? undefined : () => void edit(m)}
            onCancel={readOnly ? undefined : () => void cancel(m.writeId)}
          >
            {m.message}
          </OperatorNote>
        </LogRow>
      ))}
    </>
  )
}

/**
 * One entry in the reasoning log: a small monospace timestamp (or a live pulse
 * for the in-flight turn) on its own line, with the content full-width beneath
 * it, and a hairline separating it from the entry above.
 */
function LogRow({
  at,
  divided,
  live,
  children
}: {
  at?: string
  divided?: boolean
  live?: boolean
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1 py-3', divided && 'border-t border-border/60')}>
      {live ? (
        <span
          aria-label="streaming"
          className="size-1.5 animate-pulse rounded-full bg-primary/80"
        />
      ) : (
        <time
          className="font-mono text-10 leading-none tabular-nums text-muted-foreground/60"
          title={at ? new Date(at).toLocaleString() : undefined}
        >
          {formatLogTime(at)}
        </time>
      )}
      <div className="min-w-0">{children}</div>
    </div>
  )
}

/**
 * One committed chain-of-thought block from the durable transcript. A
 * `source` tag marks subagent turns (section parsers, the reconciler); the
 * lead's own turns render untagged.
 */
function CotBlock({ text, source }: { text: string; source?: string }) {
  return (
    <div className="min-w-0">
      {source ? (
        <div className="mb-0.5 text-10 font-semibold tracking-wide text-muted-foreground uppercase">
          {source}
        </div>
      ) : null}
      <div className="chat-markdown sidebar-chat ingest-cot min-w-0 text-foreground">
        <LazyChatMarkdown>{text}</LazyChatMarkdown>
      </div>
    </div>
  )
}

/** An operator-authored note in the log — a subtly tinted block tagged "You",
 * distinct from the agent's reasoning without leaving the log layout. Still-
 * queued notes keep cancel / edit until the agent drains them. */
function OperatorNote({
  children,
  queued,
  onEdit,
  onCancel,
  cancelling
}: {
  children: React.ReactNode
  queued?: boolean
  onEdit?: () => void
  onCancel?: () => void
  cancelling?: boolean
}) {
  return (
    <div className="rounded-md border-l-2 border-primary/40 bg-primary/[0.05] px-2.5 py-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="text-10 font-semibold tracking-wide text-primary/70 uppercase">
          You{queued ? ' · Queued' : ''}
        </div>
        {onCancel ? (
          <div className="flex items-center gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="xs"
              disabled={cancelling}
              onClick={onEdit}
              title="Edit"
              aria-label="Edit queued message"
            >
              <Pencil className="size-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              disabled={cancelling}
              onClick={onCancel}
              title="Cancel"
              aria-label="Cancel queued message"
            >
              <X className="size-3" />
            </Button>
          </div>
        ) : null}
      </div>
      <div className="mt-0.5 text-sm break-words whitespace-pre-wrap text-foreground">
        {children}
      </div>
    </div>
  )
}

/**
 * The in-flight streaming chain-of-thought for the current turn. Uses the same
 * dense markdown styling as a committed CoT block so it transitions seamlessly
 * once the turn commits and the polled transcript renders it; the live pulse in
 * the log gutter marks it as still streaming.
 */
function LiveCot({ text }: { text: string }) {
  return (
    <div className="chat-markdown sidebar-chat ingest-cot min-w-0 text-foreground">
      <LazyChatMarkdown>{text}</LazyChatMarkdown>
    </div>
  )
}

/** A resolved question in the log: the agent's question, then the operator's answer. */
function AnsweredExchange({ item }: { item: AnsweredIngestQuestion }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-1.5 text-13 leading-[1.55] text-foreground">
        <MessageCircleQuestion
          aria-hidden
          className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
        />
        <span className="min-w-0 whitespace-pre-wrap">{item.question}</span>
      </div>
      <OperatorNote>
        {item.answer || <span className="text-muted-foreground italic">(no answer recorded)</span>}
      </OperatorNote>
    </div>
  )
}

/** Shared chat composer, mirroring the orchestrator sidebar composer. */
function ChatComposer({
  value,
  onChange,
  onSubmit,
  placeholder,
  submitLabel,
  busy,
  disabled = false,
  secondaryAction
}: {
  value: string
  onChange: (next: string) => void
  onSubmit: () => void
  placeholder: string
  submitLabel: string
  busy: boolean
  disabled?: boolean
  secondaryAction?: React.ReactNode
}) {
  const canSubmit = value.trim().length > 0 && !busy && !disabled

  return (
    <div>
      <Textarea
        rows={3}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            if (canSubmit) onSubmit()
          }
        }}
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-10 text-muted-foreground">
          <kbd className="font-mono">Enter</kbd>
          <span className="ml-1">to send,</span>
          <kbd className="ml-1.5 font-mono">⇧ Enter</kbd>
          <span className="ml-1">for new line</span>
        </span>
        <div className="flex items-center gap-2">
          {secondaryAction}
          <Button
            type="button"
            variant="outline"
            size="xs"
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {busy ? 'Sending…' : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

/**
 * Bottom always-on composer for unsolicited operator messages. Always mounted
 * and always enabled (controlled so "edit" on a queued message can refill it):
 * while the run is live the message is drained at the agent's next step; once
 * the run has finished, sending picks the run back up — the server re-dispatches
 * a fresh turn that recovers from the scratchpad + branch and continues.
 *
 * When exactly one question is open (`answerTarget`), submitting answers that
 * question instead — one composer serves both flows, Cursor-style, rather than
 * stacking a second textarea inside the question card.
 */
function OperatorComposer({
  jobId,
  active,
  value,
  onChange,
  bordered,
  empty,
  answerTarget,
  messageTarget,
  selectedPillKey,
  targetLabel,
  closedChild,
  onStop,
  stopping = false,
  canManage
}: {
  jobId: string
  active: boolean
  value: string
  onChange: (next: string) => void
  bordered: boolean
  empty: boolean
  answerTarget?: PendingIngestQuestion
  /** Scoped free-text target from the selected pill (empty → lead). */
  messageTarget: {
    dispatchId?: string
    agentKind?: 'ingest_section_parser' | 'ingest_reconciler'
  }
  selectedPillKey: string
  targetLabel?: string
  /** Finished child pill — free-text is closed; steer the operator to Lead. */
  closedChild?: { label: string; onSwitchToLead: () => void }
  onStop?: () => void
  stopping?: boolean
  canManage: boolean
  /** `card` adopts the chat page composer. */
  variant?: 'default' | 'card'
}) {
  const [sendMessage, { isLoading: sending }] = useSendIngestMessageMutation()
  const [answerQuestion, { isLoading: answering }] = useAnswerIngestQuestionMutation()
  const isLoading = sending || answering
  const isChildPill = selectedPillKey !== LEAD_PILL_KEY
  // Catalog-only section pills have no dispatch yet — don't mis-route to lead.
  const targetingChildWithoutDispatch = isChildPill && !messageTarget.dispatchId && !answerTarget
  const childLabel = isChildPill ? targetLabel : undefined
  // Finished children stay answerable if a question somehow remains open, but
  // free-text Send is closed — those messages would never drain.
  const composerLocked = targetingChildWithoutDispatch || (Boolean(closedChild) && !answerTarget)

  const submit = (): void => {
    const trimmed = value.trim()
    if (!trimmed || isLoading) return
    if (answerTarget) {
      void answerQuestion({ jobId, writeId: answerTarget.writeId, answer: trimmed })
        .unwrap()
        .then(() => onChange(''))
        .catch(() => {
          toast.error('Failed to send answer — try again.')
        })
      return
    }
    if (composerLocked) return
    void sendMessage({
      jobId,
      message: trimmed,
      ...(messageTarget.dispatchId ? { dispatchId: messageTarget.dispatchId } : {}),
      ...(messageTarget.agentKind ? { agentKind: messageTarget.agentKind } : {})
    })
      .unwrap()
      .then(() => onChange(''))
      .catch(() => {
        toast.error('Failed to send message — try again.')
      })
  }

  return (
    <div className={cn('px-4 py-3', bordered && 'border-t border-border')}>
      {closedChild && !answerTarget ? (
        <p className="m-0 mb-2 text-xs text-muted-foreground">
          {closedChild.label} finished — message the lead instead.{' '}
          <button
            type="button"
            className="font-medium text-foreground underline-offset-2 hover:underline"
            onClick={closedChild.onSwitchToLead}
          >
            Switch to Lead
          </button>
        </p>
      ) : !active && !isChildPill ? (
        <p className="m-0 mb-2 text-xs text-muted-foreground">
          This run has finished — send a message to pick it back up.
        </p>
      ) : targetingChildWithoutDispatch ? (
        <p className="m-0 mb-2 text-xs text-muted-foreground">
          Waiting for {childLabel ?? 'this agent'} to start before free-text can be routed here.
        </p>
      ) : empty ? (
        <p className="m-0 mb-2 text-xs text-muted-foreground">
          No questions yet — message {childLabel ?? 'the agent'} any time. It reads this on its next
          step.
        </p>
      ) : null}
      <ChatComposer
        value={value}
        onChange={onChange}
        onSubmit={submit}
        placeholder={
          closedChild
            ? `${closedChild.label} finished — switch to Lead to message…`
            : answerTarget
              ? `Answer ${childLabel ?? 'the agent'}…`
              : targetingChildWithoutDispatch
                ? 'Waiting for this agent to start…'
                : childLabel
                  ? `Message ${childLabel}…`
                  : active
                    ? 'Message the agent…'
                    : 'Send to continue this run…'
        }
        submitLabel={
          answerTarget ? 'Answer' : active || messageTarget.dispatchId ? 'Send' : 'Continue'
        }
        busy={isLoading}
        disabled={composerLocked}
        secondaryAction={
          active && onStop && !isChildPill ? (
            <WriteButton
              canManage={canManage}
              type="button"
              size="xs"
              variant="outline"
              onClick={onStop}
              disabled={stopping}
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              title="Stop this ingest job and keep the branch"
            >
              <Square className="size-3 fill-current" />
              {stopping ? 'Stopping…' : 'Stop ingest'}
            </WriteButton>
          ) : undefined
        }
      />
    </div>
  )
}

/**
 * An open question awaiting the operator. Styled as a warn-accented log entry
 * (not a chat bubble): compact header, question at the CoT type size, option
 * buttons inline. `showComposer` adds a dedicated answer box — only used when
 * several questions are open at once; the single-question case answers
 * through the page's bottom composer instead.
 */
function OpenQuestion({
  jobId,
  question,
  showComposer
}: {
  jobId: string
  question: PendingIngestQuestion
  showComposer: boolean
}) {
  const [answer, setAnswer] = React.useState('')
  const [answerQuestion, { isLoading }] = useAnswerIngestQuestionMutation()

  const submit = (value: string): void => {
    const trimmed = value.trim()
    if (!trimmed || isLoading) return
    void answerQuestion({ jobId, writeId: question.writeId, answer: trimmed })
      .unwrap()
      .then(() => setAnswer(''))
      .catch(() => {
        toast.error('Failed to send answer — try again.')
      })
  }

  return (
    <div
      id={ingestQuestionElementId(question.writeId)}
      data-ingest-question={question.writeId}
      tabIndex={-1}
      className="flex min-w-0 flex-col gap-2 rounded-lg border-l-2 border-st-warn/60 bg-st-warn/[0.04] py-2 pr-2 pl-3"
    >
      <div className="flex items-center gap-1.5 text-st-warn">
        <MessageCircleQuestion aria-hidden className="size-3.5 shrink-0" />
        <span className="text-10 font-semibold tracking-wide uppercase">Needs your input</span>
      </div>

      <div className="text-13 leading-[1.55] break-words whitespace-pre-wrap text-foreground">
        {question.question}
      </div>
      {question.context ? (
        <div className="text-xs leading-[1.5] break-words whitespace-pre-wrap text-muted-foreground">
          {question.context}
        </div>
      ) : null}

      {question.options && question.options.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {question.options.map((opt) => (
            <Button
              key={opt}
              type="button"
              variant="outline"
              size="xs"
              disabled={isLoading}
              onClick={() => submit(opt)}
              className="h-auto max-w-full justify-start rounded-full bg-background px-3 py-1 text-left text-xs font-normal break-words whitespace-normal"
            >
              {opt}
            </Button>
          ))}
        </div>
      ) : null}

      {showComposer ? (
        <ChatComposer
          value={answer}
          onChange={setAnswer}
          onSubmit={() => submit(answer)}
          placeholder="Type your answer…"
          submitLabel="Send answer"
          busy={isLoading}
        />
      ) : null}
    </div>
  )
}

export default Conversation
